import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Building2, Users, FileText, Map, BarChart3,
  CheckCircle, ArrowRight, Zap,
  MessageSquare, Bell, Calendar,
  Target, TrendingUp, FileCheck, AlertTriangle, Menu, X
} from 'lucide-react';
import SEO from '../components/SEO';
import './FeaturesPage.css';

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  useEffect(() => {
    // Scroll to anchor if hash is present
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const detailedFeatures = [
    {
      id: 'site-analysis',
      icon: Map,
      title: 'Automated Site Compliance Check',
      tagline: 'Automated site compliance with Live County GIS Data Feed for terrain mapping, verified setback calculations, and instant zoning verification',
      description: 'Upload any property address or coordinates and get instant site compliance verification. Our system connects directly to Live County GIS Data Feed to analyze terrain, calculate setbacks, verify zoning compliance, and provide detailed site reports in seconds.',
      benefits: [
        'Direct integration with Live County GIS Data Feed for verified accuracy',
        'Verified setback calculations based on local zoning codes',
        'System verifies zoning compliance and building restrictions',
        'Real-time parcel boundary detection',
        'Terrain elevation analysis and slope calculations',
        'Generate comprehensive site reports instantly'
      ],
      features: [
        { icon: Map, text: 'Live County GIS Data Feed' },
        { icon: Target, text: 'Verified setback calculations' },
        { icon: CheckCircle, text: 'Zoning compliance verification' },
        { icon: Zap, text: 'Automated site recommendations' }
      ],
      image: '/images/site-analysis.png'
    },
    {
      id: 'floor-plans-cad',
      icon: FileText,
      title: 'Floor Plans & CAD',
      tagline: 'Upload CAD files and selections - system automatically identifies and places rooms with smart layout optimization',
      description: 'Upload your CAD files and let the system do the heavy lifting. Automatically identifies rooms, places selections, and optimizes layouts. No more manual data entry or room-by-room configuration.',
      benefits: [
        'Upload CAD files in multiple formats (DWG, DXF, PDF)',
        'Automatically detects and labels rooms (kitchen, bathroom, bedroom, etc.)',
        'Upload your selections and system places them in the correct rooms',
        'Intelligent layout optimization suggestions',
        'Real-time 3D visualization of floor plans',
        'Export updated plans with selections placed'
      ],
      features: [
        { icon: FileText, text: 'Multi-format CAD file support' },
        { icon: Zap, text: 'Automated room detection' },
        { icon: Target, text: 'Smart selection placement' },
        { icon: FileCheck, text: 'Automated layout optimization' }
      ],
      image: '/images/floor-plans-cad.png'
    },
    {
      id: 'ai-communications',
      icon: MessageSquare,
      title: 'Automated Project Communications',
      tagline: 'Assistant handles calls, schedules appointments, transfers to team members, composes emails, and replies to messages',
      description: 'Your 24/7 communication assistant handles everything - from answering calls and scheduling appointments to composing emails and replying to messages. Never miss an important call or message.',
      benefits: [
        'Assistant handles inbound calls and schedules appointments automatically',
        'Transfer calls to the right team member based on context',
        'Assistant composes new emails with proper context and tone',
        'Intelligent email reply suggestions based on content',
        'Automated message replies across all platforms',
        'Natural language understanding for complex requests'
      ],
      features: [
        { icon: MessageSquare, text: 'Automated call handling & scheduling' },
        { icon: Bell, text: 'Smart call transfer to team' },
        { icon: FileText, text: 'Automated email composer & replies' },
        { icon: Zap, text: 'Automated message responses' }
      ],
      image: '/images/ai-communications.png'
    },
    {
      id: 'vendor-bidding',
      icon: Users,
      title: 'Smart Vendor Bidding',
      tagline: 'Send bid requests with CAD and selections, receive bids, and compare line items based on past performance',
      description: 'Streamline your vendor bidding process with intelligent automation. Send detailed bid requests including CAD files and selections, receive competitive bids, and compare line items based on historical performance data.',
      benefits: [
        'Send bid requests with embedded CAD files and selections',
        'Include all project details and specifications automatically',
        'Receive and organize bids from multiple vendors',
        'Compare line items side-by-side across vendors',
        'System analyzes past vendor performance for recommendations',
        'Track bid history and vendor reliability metrics'
      ],
      features: [
        { icon: Users, text: 'Automated bid request generation' },
        { icon: FileText, text: 'CAD & selection integration' },
        { icon: BarChart3, text: 'Line-by-line bid comparison' },
        { icon: TrendingUp, text: 'Performance-based recommendations' }
      ],
      image: '/images/vendor-bidding.png'
    },
    {
      id: 'phases-budget',
      icon: BarChart3,
      title: 'Smart Phases & Budget',
      tagline: 'Track budgets, analyze invoices, monitor phase completion, and auto-schedule vendors based on lead times',
      description: 'Intelligent project phase management with automated budget tracking and vendor coordination. The system monitors phase completion, analyzes invoices, and automatically schedules vendors based on lead times - even following up via messages and calls.',
      benefits: [
        'Track budget across all project phases with automated analysis',
        'System automatically analyzes and categorizes invoices',
        'Visual charts show phase completion and progress',
        'System monitors phase timelines based on vendor lead times',
        'Automatically messages vendors to schedule when phase approaching',
        'System confirms day before via message (2 attempts), then calls if needed'
      ],
      features: [
        { icon: BarChart3, text: 'Phase tracking & budget analytics' },
        { icon: FileCheck, text: 'Automated invoice analysis' },
        { icon: Calendar, text: 'Smart vendor scheduling' },
        { icon: MessageSquare, text: 'Automated vendor confirmations' }
      ],
      image: '/images/phases-budget.png'
    },
    {
      id: 'ai-photo-notes',
      icon: Building2,
      title: 'Automated Site Progress Logging',
      tagline: 'Take site photos and get instant detailed notes, progress tracking, and automated issue detection',
      description: 'Transform how you document construction sites. Simply take photos and the system instantly generates detailed notes, tracks progress, identifies potential issues, and organizes everything automatically.',
      benefits: [
        'Take photos anywhere on site with mobile device',
        'System instantly analyzes photos and generates detailed notes',
        'Automatic issue detection (defects, safety concerns, etc.)',
        'Progress tracking with before/after comparisons',
        'System categorizes and organizes photos by room/area',
        'Smart tagging and searchable photo library'
      ],
      features: [
        { icon: Building2, text: 'Instant photo analysis' },
        { icon: FileText, text: 'Auto-generated detailed notes' },
        { icon: AlertTriangle, text: 'Automated issue detection' },
        { icon: TrendingUp, text: 'Progress tracking & comparisons' }
      ],
      image: '/images/ai-photo-intelligence-real.png'
    }
  ];

  return (
    <div className="features-page">
      <SEO
        title="Features - AI-Powered Construction Tools | HomeQuest Tech"
        description="Explore powerful features: AI site compliance check, automated communications, smart vendor bidding, 3D floor plans, budget tracking, and real-time collaboration. Everything you need to manage construction projects efficiently."
        keywords="construction features, AI construction tools, site compliance software, automated project management, vendor bidding system, 3D floor plans, construction budget tracking, GIS analysis"
        canonical="/features"
      />
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="features-nav"
      >
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>

          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a href="#features">Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">
              Sign In
            </button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">
              Start Free Trial
            </button>
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="features-hero">
        <div className="hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-content"
          >
            <div className="hero-badge">
              <Zap className="badge-icon" />
              <span>COMPREHENSIVE FEATURES</span>
            </div>
            <h1 className="hero-title">
              Everything you need to
              <span className="gradient-text"> build successfully</span>
            </h1>
            <p className="hero-subtitle">
              Powerful, integrated tools designed specifically for construction professionals.
              From Live County GIS Data to automated project management, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Feature Details */}
      {detailedFeatures.map((feature, index) => (
        <section
          key={feature.id}
          id={feature.id}
          className={`feature-detail ${index % 2 === 1 ? 'reverse' : ''}`}
        >
          <div className="feature-container">
            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="feature-content"
            >
              <div className="feature-icon-large">
                <feature.icon />
              </div>
              <h2 className="feature-title-large">{feature.title}</h2>
              <p className="feature-tagline">{feature.tagline}</p>
              <p className="feature-description-long">{feature.description}</p>

              <div className="feature-benefits">
                <h3>Key Benefits</h3>
                <ul>
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx}>
                      <CheckCircle className="check-icon" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="feature-highlights">
                {feature.features.map((item, idx) => (
                  <div key={idx} className="highlight-item">
                    <item.icon className="highlight-icon" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="feature-visual"
            >
              <div className="ipad-mockup">
                <div className="ipad-camera"></div>
                <div className="ipad-screen">
                  <div className="feature-image-container">
                    <img
                      src={feature.image}
                      alt={`${feature.title} - ${feature.tagline}`}
                      className="feature-screenshot"
                      onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const placeholder = target.nextElementSibling as HTMLElement;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                    <div className="feature-image-placeholder" style={{ display: 'none' }}>
                      <feature.icon className="placeholder-icon" />
                      <div className="placeholder-text">Feature Preview</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="features-cta">
        <div className="cta-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2 className="cta-title">Ready to experience all these features?</h2>
            <p className="cta-subtitle">
              Join our beta program and lock in 30% off for life. Limited to 10 founding members.
            </p>
            <div className="cta-buttons">
              <button
                onClick={() => navigate('/pricing')}
                className="cta-primary large"
              >
                Start Your Free Trial
                <ArrowRight className="cta-icon" />
              </button>
              <button
                onClick={() => navigate('/demo')}
                className="cta-secondary large"
              >
                Book a Demo
              </button>
            </div>
            <p className="cta-note">
              <CheckCircle className="check-small" />
              2-week trial • Credit card required • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="features-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech™. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Home</a>
              <a onClick={() => navigate('/pricing')} style={{ cursor: 'pointer' }}>Pricing</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/'); }} style={{ cursor: 'pointer' }}>Privacy</a>
              <a onClick={(e) => { e.preventDefault(); navigate('/'); }} style={{ cursor: 'pointer' }}>Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
